import { useEffect, useRef, useState } from "react";
import { ShieldAlert, Fingerprint } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PinInput } from "@/components/PinInput";
import { registerSuperPinPrompt, errMsg } from "@/lib/api";
import { approvalAssert, approvalBioHere, bioCancelled, forgetApprovalHere } from "@/lib/biometric";

export const SuperPinPrompt = () => {
  const [req, setReq] = useState(null);
  const [pin, setPin] = useState("");
  const [bioErr, setBioErr] = useState("");
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);
  const bio = req && approvalBioHere();

  useEffect(() => {
    registerSuperPinPrompt((r) => new Promise((resolve) => {
      resolver.current = resolve;
      setPin("");
      setBioErr("");
      setReq(r);
    }));
    return () => registerSuperPinPrompt(null);
  }, []);

  const finish = (value) => {
    resolver.current?.(value);
    resolver.current = null;
    setReq(null);
    setPin("");
  };

  const approveBio = async () => {
    setBusy(true);
    setBioErr("");
    try {
      finish({ bio: await approvalAssert(req.method, req.path) });
    } catch (e) {
      if (e?.response?.status === 404) forgetApprovalHere();
      if (!bioCancelled(e)) setBioErr(e?.response ? errMsg(e) : "Sidik jari tidak dikenali. Gunakan PIN.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && !busy && finish(null)}>
      <DialogContent className="max-w-sm z-[100]" data-testid="super-pin-dialog">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-gradient-to-br from-rose-600 to-orange-500 text-white flex items-center justify-center"><ShieldAlert className="h-6 w-6" /></div>
          <DialogTitle className="text-center">Persetujuan Diperlukan</DialogTitle>
          <DialogDescription className="text-center">
            {bio ? "Gunakan sidik jari atau PIN Super Admin" : "Masukkan PIN Super Admin"} untuk menyetujui: <b className="text-slate-900" data-testid="super-pin-action">{req?.action}</b>
          </DialogDescription>
        </DialogHeader>
        {bio && (
          <button onClick={approveBio} disabled={busy} data-testid="super-pin-biometric"
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors">
            <Fingerprint className="h-5 w-5" /> {busy ? "Memeriksa..." : "Setujui dengan Sidik Jari"}
          </button>
        )}
        {bioErr && <p className="text-sm text-center text-red-600 font-medium" data-testid="super-pin-bio-error">{bioErr}</p>}
        <PinInput value={pin} onChange={setPin} onComplete={finish} testid="super-pin-input" autoFocus={!bio} />
        {req?.error && <p className="text-sm text-center text-red-600 font-medium" data-testid="super-pin-error">{req.error}</p>}
        <div className="flex gap-2">
          <button onClick={() => finish(null)} disabled={busy} data-testid="super-pin-cancel" className="flex-1 border border-slate-200 hover:bg-slate-100 py-2.5 rounded-xl text-sm font-semibold">Batal</button>
          <button onClick={() => finish(pin)} disabled={busy || pin.length !== 6} data-testid="super-pin-submit" className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold">Setujui</button>
        </div>
        <p className="text-[11px] text-center text-slate-500">Setiap perubahan dicatat di Log Perubahan.</p>
      </DialogContent>
    </Dialog>
  );
};
